import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiActions, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  COLORS,
  MODAL_TITLES,
} from "@miplatform/constants";
import { Box } from "@mui/material";

/**
 * MiBuildWeb standard footer component with action buttons.
 * @component
 * @param {Object} props - The component props
 * @param {boolean} [props.show=false] - Whether to show the footer
 * @param {number} [props.height=72] - Default Height of the footer
 * @param {string} [props.saveTitle="Save"] - Text for the save button
 * @param {string} [props.cancelTitle="Cancel"] - Text for the cancel button
 * @param {boolean} [props.sticky=false] - Whether the footer should be sticky
 * @param {Function} [props.handleSaveClick] - Click handler for save button
 * @param {Function} [props.handleCancelClick] - Click handler for cancel button
 * @param {boolean} [props.disableSave] - Whether to disable the save button
 * @param {boolean} [props.disableCancel] - Whether to disable the cancel button
 * @param {Array} [props.actions=[]] - Custom actions to override default save/cancel buttons.
 * @param {boolean} [props.isDirty=false] - Whether there are unsaved changes
 * Each action should have: {name, variant, onClick, disabled, color (optional)}
 * @returns {JSX.Element} Footer component with action buttons
 */

export function Footer({
  show = false,
  height = 50,
  saveTitle = "Save",
  cancelTitle = "Cancel",
  sticky = false,
  handleSaveClick,
  handleCancelClick,
  disableSave,
  disableCancel,
  actions = [],
  isDirty = false,
  cancelTitleIcon,
}) {
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const { classes } = useStyles({ show, sticky, height });

  const footerActions = actions.length
    ? actions
    : [
        {
          name: cancelTitle,
          variant: BUTTON_VARIANTS.TEXT,
          color: COLORS.BLACK,
          onClick: () => {
            if (isDirty) {
              setOpenCancelModal(true);
            } else {
              handleCancelClick();
            }
          },
          disabled: disableCancel || !handleCancelClick,
        },
        {
          name: saveTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSaveClick,
          disabled: disableSave || !handleSaveClick,
        },
      ];

  return (
    <Box className={classes.root}>
      <MiActions actions={footerActions} />
      <MiModalConfirm
        titleIcon={cancelTitleIcon}
        title={MODAL_TITLES.CANCEL}
        textPositive="Confirm"
        open={openCancelModal}
        handleNegative={() => setOpenCancelModal(false)}
        handlePositive={() => {
          handleCancelClick();
          setOpenCancelModal(false);
        }}
        description="You have unsaved changes. Are you sure you want to cancel?"
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme, { show, sticky, height }) => {
  const stickyStyle = {
    position: "sticky",
    bottom: 0,
    zIndex: theme.zIndex.snackbar,
    border: "none",
  };

  return {
    root: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      paddingRight: theme.spacing(3),
      borderTop: `1px solid ${theme.palette.grey.accent}`,
      backgroundColor: theme.palette.common.white,
      ...(sticky && stickyStyle),
      gap: theme.spacing(2),
      height: show ? height : 0,
      flexShrink: 0,
      opacity: show ? 1 : 0,
      overflow: "hidden",
      transition: "height 0.4s ease-in-out, opacity 0.4s ease-in-out",
    },
  };
});
